// NOTE:
// This hook is intended to be a centralized provider of such
// "API action Segment events" for easier managing.
// Such events (can also be the same ones as provided here) are also being
// triggered in other places in the app and this discrepancy is ok for now.
// This mechanism is supposed to handle only the events within the invoicing
// domain features - but maybe with time we'll build further on that.
import { useCallback, useMemo } from 'react'

import { useOrganizationSource, useSegment } from '../../../hooks'
import { InvoiceDocumentType } from '../enums/invoiceDocumentType'
import { PaymentMethodsTrackingOptions } from '../utils/getPaymentMethodsTrackingOptions'
import { isDocumentTypeCreditNote } from '../utils/isDocumentTypeCreditNote'
import { isDocumentTypeQuote } from '../utils/isDocumentTypeQuote'

enum ApiActionEvent {
  ContactCreated = 'Contact Created (Client)',
  CreditNoteApproved = 'Invoice Credit Note Approved (Client)',
  InvoiceApproved = 'Invoice Approved (Client)',
  InvoiceDraftCreated = 'Invoice Draft Created (Client)',
  InvoiceSent = 'Invoice Sent (Client)',
  ProductCreated = 'Product Created (Client)',
  QuoteCreated = 'Quote Created (Client)',
}

interface DocumentSentEventOptions {
  isStripePaymentEnabled?: boolean
}

// To keep aligned with the past data, we're mapping
// document types to already used values when necessary
const documentTypeToEventType: Partial<Record<InvoiceDocumentType, string>> = {
  [InvoiceDocumentType.CreditNote]: 'credit_note',
  [InvoiceDocumentType.Invoice]: InvoiceDocumentType.Invoice,
  [InvoiceDocumentType.Quote]: InvoiceDocumentType.Quote,
}

// We're using 'unknown' to clearly indicate there was no match here,
// as opposed to an undefined/empty value that might occur in some other cases
const getSegmentEventDocumentType = (documentType: InvoiceDocumentType) =>
  documentTypeToEventType[documentType] || 'unknown'

// USAGE:
// Call a relevant namespaced event sending function exposed by this hook
// Example: sendEvent.product.created()
export const useInvoicingApiActionSegmentEvents = () => {
  const { track } = useSegment()
  const { isSourceZervant } = useOrganizationSource()

  const trackSegmentEvent = useCallback(
    (...args: Parameters<typeof track>) => {
      if (isSourceZervant) {
        track(...args)
      }
    },
    [isSourceZervant, track],
  )

  // ----------------
  // DOCUMENT EVENTS:
  // ----------------
  const sendInvoiceDraftCreatedEvent = useCallback(
    (documentType: InvoiceDocumentType) => {
      trackSegmentEvent(ApiActionEvent.InvoiceDraftCreated, {
        type: getSegmentEventDocumentType(documentType),
      })
    },
    [trackSegmentEvent],
  )

  const sendDocumentApprovedEvent = useCallback(
    (documentType: InvoiceDocumentType, id: number, paymentMethodsTrackingOptions?: PaymentMethodsTrackingOptions) => {
      const isCreditNote = isDocumentTypeCreditNote(documentType)
      const isQuote = isDocumentTypeQuote(documentType)
      let eventName = ApiActionEvent.InvoiceApproved

      if (isCreditNote) {
        eventName = ApiActionEvent.CreditNoteApproved
      } else if (isQuote) {
        eventName = ApiActionEvent.QuoteCreated
      }

      const eventProps = isQuote
        ? {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            quote_id: id,
          }
        : {
            type: getSegmentEventDocumentType(documentType),
            ...(isCreditNote ? {} : paymentMethodsTrackingOptions),
          }

      trackSegmentEvent(eventName, eventProps)
    },
    [trackSegmentEvent],
  )

  const sendDocumentSentEvent = useCallback(
    (documentType: InvoiceDocumentType, { isStripePaymentEnabled }: DocumentSentEventOptions = {}) => {
      if (isDocumentTypeQuote(documentType) || isDocumentTypeCreditNote(documentType)) {
        return
      }

      trackSegmentEvent(ApiActionEvent.InvoiceSent, {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        stripe_payment_enabled: Boolean(isStripePaymentEnabled),
        type: getSegmentEventDocumentType(documentType),
      })
    },
    [trackSegmentEvent],
  )

  // ----------------
  // PRODUCT EVENTS:
  // ----------------
  const sendProductCreatedEvent = useCallback(() => {
    trackSegmentEvent(ApiActionEvent.ProductCreated)
  }, [trackSegmentEvent])

  // ----------------
  // CONTACT EVENTS:
  // ----------------
  const sendContactCreatedEvent = useCallback(() => {
    trackSegmentEvent(ApiActionEvent.ContactCreated)
  }, [trackSegmentEvent])

  // --------------
  // Action sets:
  // --------------
  const documentActions = useMemo(
    () => ({
      approved: sendDocumentApprovedEvent,
      draftCreated: sendInvoiceDraftCreatedEvent,
      sent: sendDocumentSentEvent,
    }),
    [sendDocumentApprovedEvent, sendDocumentSentEvent, sendInvoiceDraftCreatedEvent],
  )

  const productActions = useMemo(
    () => ({
      created: sendProductCreatedEvent,
    }),
    [sendProductCreatedEvent],
  )

  const contactActions = useMemo(
    () => ({
      created: sendContactCreatedEvent,
    }),
    [sendContactCreatedEvent],
  )

  const eventActionsByCategories = useMemo(
    () => ({
      contact: contactActions,
      document: documentActions,
      product: productActions,
    }),
    [contactActions, documentActions, productActions],
  )

  return {
    sendEvent: eventActionsByCategories,
  }
}
